import moment from 'moment';
import { TransferReservationBaseFiltersFormType } from '../components/ViajeTrasladoBaseFilters';

export interface CoordinateColumnVisibility {
  schedule: boolean;
  cliente: boolean;
  puntos: boolean;
  coordinationObservation: boolean;
  general_observation: boolean;
  serviceType: boolean;
  event_name: boolean;
  id: boolean;
  platformId: boolean;
  preAssignedDriver: boolean;
  passengers: boolean;
  actions: boolean;
}

export const columnsLabels: Record<keyof CoordinateColumnVisibility, string> = {
  schedule: 'Horarios',
  cliente: 'Cliente',
  puntos: 'Domicilios',
  coordinationObservation: 'Obs. Coordinación',
  general_observation: 'Obs. Chofer',
  serviceType: 'Servicio',
  event_name: 'Evento',
  id: 'ID',
  platformId: 'Plataforma',
  preAssignedDriver: 'Chofer Pre-asignado',
  passengers: 'Pasajeros',
  actions: 'Acciones'
};

export interface SortState {
  sortDir: string;
  sortBy: string;
}

export interface CoordinateReservesState {
  filters: TransferReservationBaseFiltersFormType;
  page: number;
  rowsPerPage: number;
  sort: SortState;
  columnVisibility: CoordinateColumnVisibility;
  lastAccessDate?: string; // Fecha del último acceso para detectar cambios de día
}

const STORAGE_KEY = 'coordinateReservesState';

export const getDefaultFilters = (): TransferReservationBaseFiltersFormType => ({
  dateFrom: moment().subtract(1, 'day').startOf('day'),
  dateTo: moment().hour(23).minutes(59).add(1, 'day'),
  clientId: null,
  serviceType: 'Todos',
  hasPriority: false,
  passangerName: '',
  client_type: 'AllClient',
  street: '',
  locality: '',
  phone: '',
  client_phone: '',
  reservationId: '',
  state: 'Todos',
  assigned_driver_id: null,
  pre_assigned_driver_id: null,
  driverCode: '',
  oficial_cuenta_id: null,
  authorized_id: null,
  cost_center: '',
  buy_order: '',
  sector: '',
  driver_with_close_shift: false,
  departure_time_exceeded: false,
  sent_reservations_platform: false,
  not_sent_reservations_platform: false,
  has_platform_id: false,
  is_event: false,
  is_llt_pais: false
});

export const getDefaultColumnVisibility = (): CoordinateColumnVisibility => ({
  schedule: true,
  cliente: true,
  puntos: true,
  coordinationObservation: true,
  general_observation: true,
  serviceType: true,
  event_name: true,
  id: true,
  platformId: true,
  preAssignedDriver: true,
  passengers: true,
  actions: true
});

export const getDefaultSort = (): SortState => ({
  sortDir: 'ASC',
  sortBy: 'created_at'
});

const getDefaultState = (): CoordinateReservesState => ({
  filters: getDefaultFilters(),
  page: 1,
  rowsPerPage: 100,
  sort: getDefaultSort(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<CoordinateReservesState>
): CoordinateReservesState => {
  const defaultFilters = getDefaultFilters();
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;
  
  return {
    ...getDefaultState(),
    ...storedState,
    filters: {
      ...defaultFilters,
      ...(storedState.filters || {}),
      // Si es un día nuevo, usar fechas por defecto, sino usar las guardadas
      dateFrom: isNewDay
        ? moment().subtract(1, 'day').startOf('day')
        : storedState.filters?.dateFrom
        ? moment(storedState.filters.dateFrom)
        : null,
      dateTo: isNewDay
        ? moment().hour(23).minutes(59).add(1, 'day')
        : storedState.filters?.dateTo
        ? moment(storedState.filters.dateTo)
        : null
    },
    lastAccessDate: today // Actualizar la fecha de acceso
  };
};

export const loadStateFromLocalStorage = (): CoordinateReservesState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return deserializeState(parsed);
    }
  } catch (error) {
    console.error('Error loading coordinate reserves state:', error);
  }
  return getDefaultState();
};

export const saveStateToLocalStorage = (state: CoordinateReservesState): void => {
  try {
    const stateWithDate = {
      ...state,
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving coordinate reserves state:', error);
  }
};

export const clearStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing coordinate reserves state:', error);
  }
};

// Los filtros por defecto de esta vista acotan por fecha (ayer a mañana), por
// lo que una reserva de un evento fuera de ese rango quedaría oculta. Al
// llegar desde afuera con un id puntual, se limpian esas fechas para
// garantizar que la reserva buscada sea visible.
export const clearStateAndAddFilterReservationId = (
  reservationId: number
): void => {
  try {
    clearStateFromLocalStorage();
    const state = getDefaultState();
    state.filters.dateFrom = null;
    state.filters.dateTo = null;
    state.filters.reservationId = reservationId.toString();
    saveStateToLocalStorage(state);
  } catch (error) {
    console.error('Error clearing coordinate reserves state:', error);
  }
};

